import React from 'react';
// L&T - Claude Apps (Firm): a launcher page for the firm's Claude-built apps.
// Each card links out to that app's live site (its own M365 SSO). A card with
// `soon: true` has no live URL yet - it renders greyed out with a "Soon" badge
// and isn't clickable.
//
// The repos have no distinct per-app logo files, so each app gets a designed
// icon mark here. Drop a real logo image in and swap `logo` if brand art turns up.

// Document mark - Corporate Precedents (automated document generation).
const DocLogo = () => (
  <svg width="30" height="30" viewBox="0 0 32 32" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 4h9l5 5v17a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"/>
    <path d="M18 4v5h5"/>
    <path d="M11 15h10M11 19h10M11 23h6"/>
  </svg>
);

// Traffic-light mark - Capacity Tracker (green / amber / red capacity model).
const TrafficLogo = () => (
  <svg width="30" height="30" viewBox="0 0 32 32" fill="none">
    <rect x="11" y="3" width="10" height="26" rx="5" stroke="#fff" strokeWidth="1.8"/>
    <circle cx="16" cy="9"  r="2.3" fill="#22C55E"/>
    <circle cx="16" cy="16" r="2.3" fill="#F59E0B"/>
    <circle cx="16" cy="23" r="2.3" fill="#EF4444"/>
  </svg>
);

// Clipboard-check mark - Compliance (new matter / client intake + checks).
const ComplianceLogo = () => (
  <svg width="30" height="30" viewBox="0 0 32 32" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="8" y="6" width="16" height="22" rx="2"/>
    <path d="M12 6V4.5A1.5 1.5 0 0 1 13.5 3h5A1.5 1.5 0 0 1 20 4.5V6"/>
    <path d="M12 17.5l2.5 2.5 5-5.5"/>
  </svg>
);

const APPS = [
  {
    key: 'corporate', name: 'Corporate Precedents', tagline: 'Automated M&A document generation',
    domain: 'corporatedocs.leeandthompson.com', url: 'https://corporatedocs.leeandthompson.com',
    tileBg: '#1E40AF', logo: <DocLogo/>,
  },
  {
    key: 'capacity', name: 'Capacity Tracker', tagline: 'Film & TV team capacity',
    domain: 'capacity.leeandthompson.com', url: 'https://capacity.leeandthompson.com',
    tileBg: '#0F172A', logo: <TrafficLogo/>,
  },
  {
    key: 'compliance', name: 'Compliance', tagline: 'New Matter / Client requests',
    domain: 'compliance.leeandthompson.com', url: null, soon: true,
    tileBg: '#0D9488', logo: <ComplianceLogo/>,
  },
];

const S = {
  wrap:  { padding: '20px 24px', width: '100%', boxSizing: 'border-box' },
  grid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 18, maxWidth: 1140, alignItems: 'stretch' },
  card:  { display: 'flex', flexDirection: 'column', gap: 14, padding: 20, borderRadius: 12,
           border: '1px solid var(--line)', background: 'var(--bg-card)', textDecoration: 'none',
           color: 'inherit', transition: 'border-color 120ms, box-shadow 120ms', height: '100%', boxSizing: 'border-box' },
  tile:  { width: 56, height: 56, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  name:  { fontSize: 16, fontWeight: 650, color: 'var(--text)' },
  tag:   { fontSize: 13, color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.4 },
  foot:  { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-subtle)', marginTop: 'auto' },
  badge: { marginLeft: 'auto', fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
           padding: '1px 7px', borderRadius: 999, background: 'var(--bg-sub)', color: 'var(--text-subtle)', border: '1px solid var(--line)' },
};

const OpenArrow = () => (
  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 5h8v8M15 5 5 15"/>
  </svg>
);

function AppCard({ app }) {
  const inner = (
    <>
      <div style={{ ...S.tile, background: app.tileBg }}>{app.logo}</div>
      <div>
        <div style={S.name}>{app.name}</div>
        <div style={S.tag}>{app.tagline}</div>
      </div>
      <div style={S.foot}>
        <span>{app.domain}</span>
        {app.soon ? <span style={S.badge}>Soon</span> : <OpenArrow/>}
      </div>
    </>
  );
  if (app.soon) {
    return <div style={{ ...S.card, cursor: 'default', opacity: 0.72 }}>{inner}</div>;
  }
  return (
    <a href={app.url} target="_blank" rel="noopener noreferrer"
       style={S.card}
       onMouseEnter={e => { e.currentTarget.style.borderColor = app.tileBg; }}
       onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--line)'; }}>
      {inner}
    </a>
  );
}

export function FirmAppsView() {
  return (
    <div style={S.wrap}>
      <div style={S.grid}>
        {APPS.map(app => <AppCard key={app.key} app={app}/>)}
      </div>
    </div>
  );
}
